/**
 * My attendance · Team — V9-04 §2.
 *
 * ## Why this exists
 *
 * `/attendance/team` has been built and routed since V6-04. **Nothing in the
 * application linked to it.** The sidebar had one Attendance entry pointing at
 * the personal page, and that page mentioned the team view nowhere — so the
 * only way to reach a report built specifically to answer "how are my people
 * doing" was to type the URL.
 *
 * The founder — the owner, the most motivated user there is — reported that he
 * could not see anyone. He was right, and the data was fine the whole time.
 *
 * §2: "Team is one click from the sidebar entry, never buried." Two tabs, on
 * both pages, so whichever one somebody lands on the other is visible.
 */

import { NavLink } from 'react-router';
import { PERMISSIONS } from '@virtuetasks/types';
import { useCan } from '@/features/auth';
import { cn } from '@/lib/utils';

export function AttendanceTabs() {
  const can = useCan();
  /**
   * Shown only to people who may actually open the team report.
   *
   * A tab that 403s is worse than no tab — V7-02's rule about dead controls,
   * and the reason this checks the same permission the route does rather than
   * a role name.
   */
  if (!can(PERMISSIONS.REPORT_VIEW_TEAM)) return null;

  const tab = (to: string, label: string, testId: string) => (
    <NavLink
      to={to}
      end
      data-testid={testId}
      className={({ isActive }) =>
        cn(
          'rounded-[6px] px-2.5 py-1 text-[13px]',
          isActive
            ? 'bg-[var(--accent)]/10 font-medium text-[var(--accent)]'
            : 'vt-muted hover:bg-[var(--surface-2)]',
        )
      }
    >
      {label}
    </NavLink>
  );

  return (
    <nav
      className="mb-3 flex items-center gap-1"
      data-testid="attendance-tabs"
      aria-label="Attendance views"
    >
      {tab('/attendance', 'My attendance', 'attendance-tab-mine')}
      {tab('/attendance/team', 'Team', 'attendance-tab-team')}
    </nav>
  );
}
